import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { getBookings, cancelBooking, BookingStatus } from "@/services/data";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, MapPin, Calendar, CheckCircle, XCircle, AlertCircle, FlaskConical, Loader2, Home, Building2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const statusConfig: Record<BookingStatus, { color: string; icon: typeof CheckCircle }> = {
  "Pending": { color: "bg-yellow-100 text-yellow-700", icon: Clock },
  "Confirmed": { color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  "Sample Collected": { color: "bg-purple-100 text-purple-700", icon: FlaskConical },
  "Report Ready": { color: "bg-green-100 text-green-700", icon: CheckCircle },
  "Cancelled": { color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function MyBookingsPage() {
  const { patient, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const { data: allBookings, isLoading, refetch } = useQuery({ queryKey: ["bookings"], queryFn: getBookings });

  const bookings = allBookings?.filter(b => b.patientEmail === patient?.email) || [];

  const handleCancel = async (id: string) => {
    await cancelBooking(id);
    refetch();
    toast.success("Booking cancelled");
  };

  if (!isLoggedIn || !patient) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <p className="text-muted-foreground mb-4">Please login to view your bookings</p>
        <Button onClick={() => navigate("/login")}>Login</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">My Bookings</h1>
          <p className="text-muted-foreground text-sm mt-1">Track your test appointments and reports</p>
        </div>
        <Button onClick={() => navigate("/tests")} size="sm">Book New Test</Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-xl text-muted-foreground">
          <FlaskConical className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium mb-2">No bookings yet</p>
          <Button variant="outline" onClick={() => navigate("/tests")}>Browse Tests</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map(booking => {
            const sc = statusConfig[booking.status];
            return (
              <div key={booking.id} className="bg-card border border-border rounded-xl p-5 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-semibold text-foreground text-sm">{booking.testNames.join(", ")}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Booking ID: <span className="font-mono">{booking.id}</span></p>
                  </div>
                  <Badge className={cn("border-0 text-xs shrink-0", sc.color)}>
                    <sc.icon className="h-3 w-3 mr-1" />
                    {booking.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-muted-foreground mb-4">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>{booking.slotDate} · {booking.slotTime}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {booking.collectionType === "Home Collection" ? <Home className="h-3.5 w-3.5" /> : <Building2 className="h-3.5 w-3.5" />}
                    <span>{booking.collectionType}</span>
                  </div>
                  {booking.city && (
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      <span>{booking.city}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium", booking.paymentStatus === "Paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700")}>
                      {booking.paymentStatus === "Paid" ? `Paid ₹${booking.paidAmount}` : `Due ₹${booking.totalAmount}`}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {booking.status === "Report Ready" && (
                    <Button size="sm" className="text-xs h-7">Download Report</Button>
                  )}
                  {(booking.status === "Pending" || booking.status === "Confirmed") && (
                    <Button size="sm" variant="ghost" className="text-xs h-7 text-destructive hover:text-destructive" onClick={() => handleCancel(booking.id)}>
                      Cancel Booking
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
